import React, { useState, useEffect } from 'react'
import styles from './MediaPage.module.scss'
import { 
  getPressArticles,
  getNews,
  getVideoReviews,
  getLetsPlayVideos,
  getStreamers,
  searchAllMedia
} from '../../../service/MediaService'
import { useAuth } from '../../../contexts/AuthContext'
import { GameControllerIcon, StarIcon, PeopleIcon, HeartIcon, EyeIcon, SearchIcon, DocumentIcon, FilmIcon, TVIcon, ClockIcon } from '../../../components/CustomIcons'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import MediaModal from './components/MediaModal/MediaModal'

const MediaPage = ({ embedded = false, goBack }) => {
  // eslint-disable-next-line no-unused-vars
  const { user, isAuthenticated } = useAuth()
  const [content, setContent] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [activeTab, setActiveTab] = useState('all') // all, press, news, reviews, letsplay, streamers
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [showMediaModal, setShowMediaModal] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    content_type: '',
    date_range: '',
    rating_min: ''
  })
  const [sortBy, setSortBy] = useState('-created_at')

  useEffect(() => {
    loadContent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, sortBy, filters])

  // Функция применения фильтров
  const applyFilters = () => {
    setCurrentPage(1)
    loadContent()
  }

  const loadContent = async () => {
    try {
      setLoading(true)
      let response
      
      if (filters.search) {
        // Если есть поисковый запрос, используем универсальный поиск
        const searchResults = await searchAllMedia(filters.search)
        const allResults = [
          ...(searchResults.press || []),
          ...(searchResults.news || []),
          ...(searchResults.reviews || []),
          ...(searchResults.letsplay || []),
          ...(searchResults.streamers || [])
        ]
        
        // Фильтруем по активной вкладке
        let filteredResults = allResults
        if (activeTab !== 'all') {
          const typeMap = {
            'press': searchResults.press || [],
            'news': searchResults.news || [],
            'reviews': searchResults.reviews || [],
            'letsplay': searchResults.letsplay || [],
            'streamers': searchResults.streamers || []
          }
          filteredResults = typeMap[activeTab] || []
        }
        
        setContent(filteredResults)
        setTotalPages(1)
      } else {
        // Загружаем контент по типу
        const params = {
          page: currentPage,
          ordering: sortBy
        }
        
        switch (activeTab) {
          case 'press':
            response = await getPressArticles(params)
            break
          case 'news':
            response = await getNews(params)
            break
          case 'reviews':
            response = await getVideoReviews(params)
            break
          case 'letsplay':
            response = await getLetsPlayVideos(params)
            break
          case 'streamers':
            response = await getStreamers(params)
            break
          default:
            // Для 'all' загружаем все типы контента
            const [pressRes, newsRes, reviewsRes, letsplayRes, streamersRes] = await Promise.all([
              getPressArticles({ ...params, limit: 3 }).catch(() => ({ data: { results: [] } })),
              getNews({ ...params, limit: 3 }).catch(() => ({ data: { results: [] } })),
              getVideoReviews({ ...params, limit: 3 }).catch(() => ({ data: { results: [] } })),
              getLetsPlayVideos({ ...params, limit: 3 }).catch(() => ({ data: { results: [] } })),
              getStreamers({ ...params, limit: 3 }).catch(() => ({ data: { results: [] } }))
            ])
            
            const allContent = [
              ...(pressRes.data?.results || pressRes.data || []).map(item => ({ ...item, type: 'press' })),
              ...(newsRes.data?.results || newsRes.data || []).map(item => ({ ...item, type: 'news' })),
              ...(reviewsRes.data?.results || reviewsRes.data || []).map(item => ({ ...item, type: 'reviews' })),
              ...(letsplayRes.data?.results || letsplayRes.data || []).map(item => ({ ...item, type: 'letsplay' })),
              ...(streamersRes.data?.results || streamersRes.data || []).map(item => ({ ...item, type: 'streamers' }))
            ]
            
            setContent(allContent)
            setTotalPages(1)
            return
        }
        
        setContent((response.data?.results || response.data || []).map(item => ({ ...item, type: activeTab })))
        setTotalPages(response.data?.total_pages || Math.ceil((response.data?.count || 0) / 20) || 1)
      }
    } catch (err) {
      setError(`Ошибка загрузки медиа-контента: ${err.message}`)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleMediaClick = (media) => {
    setSelectedMedia(media)
    setShowMediaModal(true)
  }



  // Функция для обработки изменений фильтров
  // eslint-disable-next-line no-unused-vars
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }

  const handleSearch = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrentPage(1)
  }

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'press': return <DocumentIcon size={20} />
      case 'news': return <DocumentIcon size={20} /> 
      case 'reviews': return <FilmIcon size={20} />
      case 'letsplay': return <GameControllerIcon size={20} />
      case 'streamers': return <TVIcon size={20} />
      default: return <DocumentIcon size={20} />
    }
  }

  const getContentTypeLabel = (contentType) => {
    switch (contentType) {
      case 'press': return 'Пресс-статья'
      case 'news': return 'Новость' 
      case 'reviews': return 'Видео-обзор'
      case 'letsplay': return "Let's Play"
      case 'streamers': return 'Стример'
      default: return 'Контент'
    }
  }

  const renderContent = () => {
    if (content.length === 0 && !loading) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📺</div>
          <h3>Контент не найден</h3>
          <p>Попробуйте изменить фильтры или выберите другую категорию</p>
        </div>
      )
    }

    return content.map(item => (
      <div key={item.id} className={styles.mediaCard} onClick={() => handleMediaClick(item)}>
        {item.cover_image && (
          <div className={styles.mediaCover}>
            <img src={item.cover_image} alt={item.title} />
            {(item.video_file || item.video_url) && (
              <div className={styles.playIcon}>▶</div>
            )}
          </div>
        )}
        {!item.cover_image && (
          <div className={styles.mediaIcon}>
            {getContentIcon(item.type)}
          </div>
        )}
        <div className={styles.mediaInfo}>
          <h3>{item.title || item.username || item.name}</h3>
          <p className={styles.contentType}>
            {getContentTypeLabel(item.type)}
          </p>
          {item.description && (
            <p className={styles.description}>
              {item.description.substring(0, 100)}{item.description.length > 100 ? '...' : ''}
            </p>
          )}
          <div className={styles.mediaStats}>
            {item.views_count !== undefined && <span><EyeIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{item.views_count}</span>}
            {item.rating_avg && <span><StarIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{item.rating_avg}</span>}
            {item.likes_count !== undefined && <span><HeartIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{item.likes_count}</span>}
            {item.followers_count && <span><PeopleIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{item.followers_count}</span>}
            {item.duration_min && <span><ClockIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{item.duration_min} мин</span>}
          </div>
        </div>
      </div>
    ))
  }

  if (loading && content.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка медиа-контента...</p>
      </div>
    )
  }

  return (
    <div className={styles.mediaPage}>
      {!embedded && (
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            <span className={styles.icon}>📺</span>
            Медиа и развлечения
          </h1>
          <p className={styles.pageSubtitle}>
            Новости, обзоры, стримы и другой интересный контент
          </p>
        </div>
      )}

      {/* Навигация по типам контента */}
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
          onClick={() => handleTabChange('all')}
        >
          Всё
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'press' ? styles.active : ''}`}
          onClick={() => handleTabChange('press')}
        >
          Пресса
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'news' ? styles.active : ''}`}
          onClick={() => handleTabChange('news')}
        >
          Новости
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'reviews' ? styles.active : ''}`}
          onClick={() => handleTabChange('reviews')}
        >
          Обзоры
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'letsplay' ? styles.active : ''}`}
          onClick={() => handleTabChange('letsplay')}
        >
          Let's Play
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'streamers' ? styles.active : ''}`}
          onClick={() => handleTabChange('streamers')}
        >
          Стримеры
        </button>
      </div>

      {/* Поиск и фильтры */}
      <div className={styles.controls}>
        <div className={styles.topControls}>
          <input 
            type="text"
            placeholder={`Поиск ${activeTab === 'all' ? 'контента' : 
              activeTab === 'news' ? 'новостей' :
              activeTab === 'articles' ? 'статей' :
              activeTab === 'reviews' ? 'обзоров' :
              activeTab === 'videos' ? 'видео' :
              activeTab === 'streams' ? 'стримеров' : 'контента'}...`}
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
            className={styles.searchInput}
          />
          
          <button 
            onClick={applyFilters}
            className={styles.searchButton}
          >
            <SearchIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Найти
          </button>
          
          <div className={styles.sortSelect}>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className={styles.select}
            >
              <option value="-created_at">Новые</option>
              <option value="created_at">Старые</option>
              <option value="-likes_count">По популярности</option>
              <option value="-views_count">По просмотрам</option>
              <option value="-rating_avg">По рейтингу</option>
              <option value="title">По названию</option>
            </select>
          </div>
        </div>

        <div className={styles.mediaFilters}>
          <div className={styles.filterGroup}>
            <label>Тип контента:</label>
            <select 
              value={activeTab} 
              onChange={(e) => handleTabChange(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">📰 Весь контент</option>
              <option value="press">📄 Пресс-релизы</option>
              <option value="news">📰 Новости</option>
              <option value="articles">Статьи</option>
              <option value="reviews">Обзоры</option>
              <option value="videos">🎥 Видео</option>
              <option value="streams">Стримы</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          <p>{error}</p>
          <button onClick={loadContent} className={styles.retryButton}>
            Попробовать снова
          </button>
        </div>
      )}

      {/* Статистика */}
      <div className={styles.stats}>
        <span>Найдено: <strong>{content.length}</strong> элементов</span>
      </div>

      {/* Сетка контента */}
      <div className={`${styles.contentGrid} ${styles[activeTab]}`}>
        {renderContent()}
      </div>

      {loading && (
        <div className={styles.loadingMore}>
          <Loader size="medium" />
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Модальное окно для просмотра медиа */}
      <MediaModal
        media={selectedMedia}
        open={showMediaModal}
        onClose={() => setShowMediaModal(false)}
      />
    </div>
  )
}

export default MediaPage